import React, { useMemo } from 'react';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import CalendarEventPill from './CalendarEventPill';
import { dayKey, eventsByDay } from './calendarDateUtils';
import type { CalendarEvent } from '../../types/integrations';

interface CalendarMonthViewProps {
  cursorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

const WEEK_OPTIONS = { weekStartsOn: 1 as const };
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_VISIBLE_PILLS = 3;

const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  cursorDate,
  events,
  onEventClick,
  onDayClick,
}) => {
  const days = useMemo(() => {
    const monthStart = startOfMonth(cursorDate);
    const monthEnd = endOfMonth(cursorDate);
    return eachDayOfInterval({
      start: startOfWeek(monthStart, WEEK_OPTIONS),
      end: endOfWeek(monthEnd, WEEK_OPTIONS),
    });
  }, [cursorDate]);

  const eventMap = useMemo(() => eventsByDay(events), [events]);

  return (
    <div className="flex flex-col h-full">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-border-primary/15">
        {DAY_HEADERS.map((label) => (
          <div
            key={label}
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-text-secondary"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div
        className="grid flex-1"
        style={{
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridAutoRows: 'minmax(110px, 1fr)',
        }}
      >
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursorDate);
          const today = isToday(day);
          const dayEvents = eventMap.get(dayKey(day)) ?? [];
          const visible = dayEvents.slice(0, MAX_VISIBLE_PILLS);
          const overflow = dayEvents.length - visible.length;

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onDayClick?.(day)}
              className={[
                'group relative flex flex-col gap-1 px-1.5 py-1.5 text-left',
                'border-r border-b border-border-primary/15 last-of-row:border-r-0',
                inMonth ? 'bg-surface-primary' : 'bg-background',
                'hover:bg-action-hover transition-colors',
              ].join(' ')}
            >
              <div className="flex items-center justify-end">
                <span
                  className={[
                    'text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[22px] text-center',
                    today
                      ? 'bg-error-500 text-white font-bold'
                      : inMonth
                        ? 'text-text-primary'
                        : 'text-text-tertiary',
                  ].join(' ')}
                >
                  {format(day, 'd')}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 min-h-0">
                {visible.map((event) => (
                  <CalendarEventPill
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                  />
                ))}
                {overflow > 0 && (
                  <span className="text-[11px] font-medium text-text-secondary pl-2 mt-0.5">
                    +{overflow} more
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarMonthView;
